import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FacebookShareButton } from 'react-share';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  ModalOverlay,
  ResponsiveContent,
  ModalCloseHeader
} from '@evdy/web-core/dist/components/shared/Modal2020';
import TextInput from '../TextInput';
import LoaderComponent from '@evdy/web-core/dist/components/shared/elements/LoaderComponent';

import { handleCopy, renderToast } from '@evdy/web-core/dist/lib/utils';
import { useHideIntercom } from '@evdy/web-core/dist/customHooks';

import { webCommunityOperations } from '@evdy/web-redux/dist/lib/webCommunity';

import fbIcon from '../../public/images/icons/facebook-social-icon@2x.png';
import emailIcon from '../../public/images/icons/email.png';
import phoneIcon from '../../public/images/icons/cell-phone-icon.svg';
import getInviteLinkIcon from '../../public/images/icons/buddy-letter-envelope.svg';

import styles from './ShareModal.module.scss';

const { getInviteLink, resetInviteLink } = webCommunityOperations;

const {
  'share-modal': shareModalClass,
  'modal2020-content-container': modalContentClass,
  'share-modal-menu': shareModalMenuClass,
  'share-links': shareLinksClass,
  'share-link': shareLinkClass,
  'copy-share-link': copyShareLinkClass,
  'input-container': inputContainerClass,
  'get-invite-link': getInviteLinkClass,
  'get-link-button': getLinkBtnClass,
  'email-form': emailFormClass
} = styles;

const copy = {
  guest: {
    header: 'View this on your computer',
    body: 'We’ll email you this link so you can easily see the details from your desktop or tablet.'
  },
  loggedIn: {
    header: 'Want to view this on your computer?',
    body: 'We’ll email you this link so you can easily see the details from your desktop or tablet.'
  }
};

const SendLink = ({ closeModal }) => {
  const dispatch = useDispatch();
  const { userData, memorialId, inviteLink } = useSelector(({ authUser, webCommunity }) => ({
    userData: authUser.data,
    memorialId: webCommunity.inviteMeta.data?._id,
    inviteLink: webCommunity.inviteLink
  }));
  const { _id: userId, email, shellEmail, meta: { posterShellEmail } = {} } = userData ?? {};
  const userEmail = email || shellEmail || posterShellEmail;
  const { success: sentLinkSuccess, error: erorrMessage, isFetching } = inviteLink;
  const emailRegex = /^\S+@\S+\.\S+$/;
  const AddPhoneFormSchema = Yup.object().shape({
    email: Yup.string().matches(emailRegex, 'Please provide a valid email')
  });
  const onSubmit = ({ email }) => {
    dispatch(getInviteLink({ userId, memorialId, email: userEmail || email }));
  };

  // show toast on success
  useEffect(() => {
    if (sentLinkSuccess) {
      const toastCopy = 'Thank you, we have sent you an email with a link to this Everdays page';
      renderToast({ id: 'get-desktop-link-success', message: toastCopy });
      dispatch(resetInviteLink());
      closeModal();
    }
    if (erorrMessage) {
      renderToast({ id: 'get-desktop-link-error', message: erorrMessage, type: 'error' });
    }
  }, [erorrMessage, sentLinkSuccess]);

  return (
    <div className={getInviteLinkClass}>
      <img src={getInviteLinkIcon} alt="" />
      <h1>{copy[!!userEmail ? 'loggedIn' : 'guest'].header}</h1>
      <p>{copy[!!userEmail ? 'loggedIn' : 'guest'].body}</p>

      {!!userEmail ? (
        <button className={getLinkBtnClass} onClick={onSubmit}>
          Email link
        </button>
      ) : (
        <Formik
          initialValues={{ email: '' }}
          validationSchema={AddPhoneFormSchema}
          onSubmit={onSubmit}
        >
          <Form className={emailFormClass}>
            <TextInput type="email" name="email" placeholder="Enter your email" />
            <button type="submit">Submit</button>
          </Form>
        </Formik>
      )}
      {isFetching && <LoaderComponent />}
    </div>
  );
};

const ShareLinks = ({ shareUrl, handleOpenSendLink }) => {
  const [wasLinkCopied, setLinkCopied] = useState(false);

  const handleMoreOptions = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          url: shareUrl,
          text: '',
          title: document?.title
        });
        console.log('share success');
      } catch (e) {
        console.error('share error', e);
      }
    }
  };

  return (
    <>
      <h1>Share this Memorial with others</h1>
      <ul className={shareLinksClass}>
        <li className={shareLinkClass}>
          <FacebookShareButton url={shareUrl}>
            <img src={fbIcon} alt="facebook" />
            Share to Facebook
          </FacebookShareButton>
        </li>
        {/* hide see more options if navigator.share doesn't exist */}
        {navigator.share && (
          <>
            <li className={shareLinkClass}>
              <button onClick={handleMoreOptions}>
                <img src={phoneIcon} alt="phone-share" />
                See more options
              </button>
            </li>
            <li className={shareLinkClass}>
              <button onClick={handleOpenSendLink}>
                <img src={emailIcon} alt="facebook" />
                Send to my email
              </button>
            </li>
          </>
        )}
      </ul>
      <div className={copyShareLinkClass}>
        <p>Tap here to copy link:</p>
        <div className={inputContainerClass}>
          <input readOnly value={shareUrl} />
          <button
            onClick={() => {
              handleCopy(shareUrl);
              setLinkCopied(true);
            }}
          >
            {wasLinkCopied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
    </>
  );
};

const ShareModal = ({ modalVisible, closeModal = () => {}, shareUrl = '' }) => {
  const [isSendLinkOpen, setIsSendLinkOpen] = useState(false);

  // hide intercom while mounted
  useHideIntercom('memorial', { showAfterClose: false });

  // reset modal state on close
  useEffect(() => {
    if (!modalVisible) {
      setIsSendLinkOpen(false);
    }
  }, [modalVisible]);

  const handleOpenSendLink = () => {
    setIsSendLinkOpen(true);
  };

  return (
    <ModalOverlay {...{ modalVisible, closeModal }} customClass={shareModalClass}>
      <ResponsiveContent floating customClass={modalContentClass}>
        <ModalCloseHeader {...{ closeModal }} />
        <div className={shareModalMenuClass}>
          {isSendLinkOpen ? (
            <SendLink {...{ closeModal }} />
          ) : (
            <ShareLinks {...{ shareUrl, handleOpenSendLink }} />
          )}
        </div>
      </ResponsiveContent>
    </ModalOverlay>
  );
};

export default ShareModal;
